'use client';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TestResultStatisticsSection } from '@/types';
import { getPerformanceColor, getSectionPercentage } from '@/utils/client/test-feedback';

export default function TestSectionPerformance({
  sections,
}: {
  sections: TestResultStatisticsSection[];
}) {
  const { t } = useTranslation();

  if (!sections.length) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.375 }}>
        {t('test.insight.sections.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('test.insight.sections.subtitle')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}>
        {sections.map((section) => {
          const { correct, questions } = section.statistics;
          const percentage = getSectionPercentage(section);
          const color = getPerformanceColor(percentage);
          const questionCount = questions || 1;
          const correctPct = (correct / questionCount) * 100;
          const sectionPr = section.statistics.percentageRanking;

          return (
            <Box
              key={section._id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                borderLeft: 4,
                borderLeftColor: color,
                bgcolor: `${color}14`,
              }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}>
                {section.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                }}>
                <Typography variant="h5" component="p" sx={{ fontWeight: 700, color }}>
                  {percentage}%
                </Typography>
                {sectionPr != null && sectionPr > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.975rem' }}>
                    {t('test.insight.sections.percentile', { pr: sectionPr.toFixed(1) })}
                  </Typography>
                )}
                {sectionPr === -1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.975rem' }}>
                    {t('test.insight.sections.insufficientData')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={correctPct}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': { bgcolor: color },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#28a745' }}>
                  {t('test.insight.sections.correct', { count: correct })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ·
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('test.insight.sections.total', { count: questions })}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
